import React, { useEffect, useState, useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { IconButton, Drawer, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
  fetchUserNotification,
  readUserNotification,
  deleteUserNotification,
  clearUserNotification,
  fetchUserNotificationCount
} from '#action/user/notification';
import PreloaderComponent from '#component/preloader';
import NotificationListItem from './list-item';
import { useIntersectionObserver } from './hooks/useIntersectionObserver';
import {
  DrawerContentCtn,
  DrawerHeader,
  DrawerTitleContainer,
  EmptyTitle,
  HeaderActions,
  StyledArrowDown,
  StyledBadge,
  StyledCircularProgress,
  StyledCloseBtn,
  StyledList,
  StyledNotificationIcon,
  StyledTitle
} from './styles';

const UserNotification = ({ socket }) => {
  const dispatch = useDispatch();
  const [openedList, setOpenedList] = useState(false);
  const account = useSelector(s => s.account, shallowEqual);
  const {
    notifications,
    status,
    unread,
    offset,
    hasMore
  } = useSelector(s => s.userNotification, shallowEqual);

  const showPreloader = status === 'loading' && !notifications.length;
  const loading = ['idle', 'loading'].includes(status);
  const isOpenedListHasMoreAndDataLoaded = openedList && hasMore && !loading;

  useEffect(() => {
    dispatch(fetchUserNotificationCount(account.id));
  }, [dispatch, account.id]);

  useEffect(() => {
    if (!socket) return false;

    const handleNotification = () => {
      dispatch(fetchUserNotification(account.id, 0));
    };

    socket.on('userNotification:ex', handleNotification);

    return () => socket.off('userNotification:ex', handleNotification);
  }, [socket, account.id, dispatch]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    dispatch(fetchUserNotification(account.id, offset));
  }, [dispatch, account.id, loading, hasMore, offset]);

  const onIntersect = useCallback(
    entries => {
      const firstEntry = entries[0];
      if (firstEntry.isIntersecting) {
        loadMore();
      }
    },
    [loadMore]
  );

  const sentinelRef = useIntersectionObserver(onIntersect, {
    root: null,
    rootMargin: '0px',
    threshold: 0
  });

  const handleOpenNotifications = () => {
    setOpenedList(true);
    dispatch(fetchUserNotification(account.id));
  };

  const handleCloseNotifications = () => {
    setOpenedList(false);
    dispatch(clearUserNotification());
  };

  const handleDelete = (notificationId, deleteAll) => {
    dispatch(deleteUserNotification(account.id, [notificationId], deleteAll));
  };

  const handleRead = (notificationId, readAll, isRead) => {
    dispatch(readUserNotification(account.id, [notificationId], readAll, isRead));
  };

  return (
    <>
      <div>
        <IconButton onClick={handleOpenNotifications}>
          <StyledBadge
            id="list-search-badge"
            color="secondary"
            badgeContent={unread}
            invisible={openedList}
            overlap="circular"
          >
            <StyledNotificationIcon />
          </StyledBadge>
        </IconButton>

      </div>
      <Drawer
        anchor="right"
        open={openedList}
        // onClose={handleOpenNotifications}
        onClose={handleCloseNotifications}
        // variant="persistent"
        slotProps={{
          backdrop: {
            style: { backgroundColor: 'transparent' }
          }
        }}
      >
        <DrawerContentCtn>
          <DrawerHeader>
            <DrawerTitleContainer>
              <StyledTitle variant="h6">
                Notifications
              </StyledTitle>
              <StyledCloseBtn aria-label="Close icon" onClick={handleCloseNotifications}>
                <CloseIcon />
              </StyledCloseBtn>
            </DrawerTitleContainer>
            {Boolean(notifications.length) && (
            <HeaderActions>
              <Button
                variant="outlined"
                startIcon={<ClearAllIcon />}
                onClick={() => handleRead(null, true)}
                disabled={!unread}
              >
                Read all
              </Button>
              <Button
                variant="outlined"
                startIcon={<DeleteForeverIcon />}
                onClick={() => handleDelete(null, true)}
              >
                Delete all
              </Button>
            </HeaderActions>
            )}
          </DrawerHeader>
          {showPreloader ? <PreloaderComponent /> : (
            notifications.length ?
              <StyledList>
                {notifications.map(notification =>
                  <NotificationListItem
                    notification={notification}
                    account={account}
                    handleDelete={handleDelete}
                    handleRead={handleRead}
                    key={notification.id}
                  />)}
                {isOpenedListHasMoreAndDataLoaded && (
                <div ref={sentinelRef} style={{ height: '1px' }} />
                )}
              </StyledList> :
              <EmptyTitle variant="h5">
                No notifications
              </EmptyTitle>
          )}
          {(loading && !showPreloader) && <StyledCircularProgress />}
          {isOpenedListHasMoreAndDataLoaded && (
            <StyledArrowDown fontSize="small" />
          )}
        </DrawerContentCtn>
      </Drawer>
    </>
  );
};

export default UserNotification;
